import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRouter } from 'next/router';

import { ProductCustomizationInput, Variant } from '__generated__/graphql';
import { MyCustomizerResponseData } from 'ui/components/ProductCustomizers';

type ProductCustomizationContextType = {
  clearUserProductCustomization: () => void;
  customizationReceived: (data: MyCustomizerResponseData) => void;
  isCustomizingProduct: boolean;
  startCustomization: () => void;
  userProductCustomization?: ProductCustomizationInput;
};

type ProductCustomizationProviderProps = {
  variation: Variant | null | undefined;
};

const ProductCustomizationContext =
  createContext<ProductCustomizationContextType>(null as any);

export const ProductCustomizationProvider: React.FC<
  ProductCustomizationProviderProps
> = ({ variation, children }) => {
  const router = useRouter();
  const [userProductCustomization, setUserProductCustomization] =
    useState<ProductCustomizationInput>();

  const isCustomizingProduct = !!router.query.customizing;

  const startCustomization = useCallback(
    () =>
      router.push(
        {
          query: {
            ...router.query,
            customizing: true,
          },
        },
        '',
        { scroll: true }
      ),
    [router]
  );

  const customizationReceived = useCallback(
    (data: MyCustomizerResponseData) => {
      // Remove customizing flag
      const { customizing, ...query } = router.query;
      router.replace({ query });

      const { designImage, productionData, id: recipeID } = data;
      const { Name: nameText, Number: numberText } = productionData.reduce(
        (ret, p) => {
          ret[p.key] = p.value;
          return ret;
        },
        {} as { [key: string]: string }
      );

      setUserProductCustomization({
        recipeId: recipeID,
        imageUrl: designImage.url,
        largeImageUrl: designImage.url, // MyCustomizer only provides one imageUrl.
        nameText,
        numberText,
      });
    },
    [router]
  );

  // Clear user input if the next variant doesn't support customization
  useEffect(() => {
    if (!variation?.myCustomizer?.enabled) {
      setUserProductCustomization(undefined);
    }
  }, [variation]);

  const clearUserProductCustomization = useCallback(() => {
    setUserProductCustomization(undefined);
  }, []);

  const providerValue = {
    clearUserProductCustomization,
    customizationReceived,
    isCustomizingProduct,
    startCustomization,
    userProductCustomization,
  };

  return (
    <ProductCustomizationContext.Provider value={providerValue}>
      {children}
    </ProductCustomizationContext.Provider>
  );
};

export const useProductCustomization = () =>
  useContext(ProductCustomizationContext);
